/**
 * API Configuration
 * Centralized configuration for Mango Business System API
 * 
 * Auto-detects environment:
 * - If running on rainbow-sails.shop → uses live VPS backend
 * - If running on localhost → uses local backend
 */

// Detect if running on production domain
const isProduction = typeof window !== 'undefined' && 
  (
    window.location.hostname === 'rainbow-sails.shop' ||
    window.location.hostname === 'www.rainbow-sails.shop' ||
    window.location.hostname === 'shop.rainbow-sails.shop' ||
    window.location.hostname === 'rainbowsales.shop' ||
    window.location.hostname === 'www.rainbowsales.shop' ||
    window.location.hostname === 'shop.rainbowsales.shop'
  );

// Production backend URL (VPS with SSL via nginx)
const PRODUCTION_API_URL = 'https://api.rainbow-sails.shop/api/v1';

// Fallback backend URL (no localhost placeholders)
const FALLBACK_API_URL = 'https://api.rainbow-sails.shop/api/v1';

// API Base URL - Auto-detect production vs development
export const API_BASE_URL = isProduction 
  ? PRODUCTION_API_URL 
  : (process.env.REACT_APP_API_URL || FALLBACK_API_URL);

// Log which environment we're using (dev only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  console.log(`🌐 [API Config] Environment: ${isProduction ? 'PRODUCTION' : 'DEVELOPMENT'}`);
  console.log(`🌐 [API Config] API URL: ${API_BASE_URL}`);
}

// Business ID must be explicitly configured per frontend deployment.
// This prevents customer records from being created under the wrong tenant.
export const BUSINESS_ID = (process.env.REACT_APP_BUSINESS_ID || '').trim();

/** Cached tenant id: same resolution as contentService (domain → API), then env fallback */
let _shopBusinessIdResolved;
let _shopBusinessIdInflight;

/**
 * Resolve the shop tenant UUID for this hostname (matches contentService / business by-domain).
 * Use for public product fetches when REACT_APP_BUSINESS_ID in the build is stale or missing.
 */
export async function resolveShopBusinessId() {
  if (_shopBusinessIdResolved !== undefined) return _shopBusinessIdResolved;
  if (_shopBusinessIdInflight) return _shopBusinessIdInflight;

  _shopBusinessIdInflight = (async () => {
    try {
      if (typeof window !== 'undefined' && window.location?.hostname) {
        const domain = window.location.hostname;
        const candidates = [
          ...new Set(
            [API_BASE_URL, 'https://api.mango-business.solutions/api/v1'].filter(Boolean)
          ),
        ];
        for (const base of candidates) {
          try {
            const response = await fetch(
              `${base}/business/by-domain?domain=${encodeURIComponent(domain)}`
            );
            if (!response.ok) continue;
            const data = await response.json();
            const resolvedId = data?.business?.id;
            if (resolvedId && typeof resolvedId === 'string') {
              const trimmed = resolvedId.trim();
              if (trimmed && trimmed !== BUSINESS_ID && BUSINESS_ID) {
                console.log(
                  `🔁 [API-CONFIG] Resolved businessId by domain (${domain}):`,
                  trimmed
                );
              }
              _shopBusinessIdResolved = trimmed;
              return trimmed;
            }
          } catch {
            // try next API host
          }
        }
      }
      _shopBusinessIdResolved = BUSINESS_ID || '';
      return _shopBusinessIdResolved;
    } finally {
      _shopBusinessIdInflight = null;
    }
  })();

  return _shopBusinessIdInflight;
}

// API Endpoints
export const API_ENDPOINTS = {
  // Products
  products: {
    public: `${API_BASE_URL}/products/public`,
    publicById: (id) => `${API_BASE_URL}/products/public/${id}`,
    byId: (id) => `${API_BASE_URL}/products/${id}`,
  },
  // Events (for future implementation)
  events: {
    public: `${API_BASE_URL}/events/public`,
    publicById: (id) => `${API_BASE_URL}/events/public/${id}`,
  },
  // Content (for future implementation)
  content: {
    public: `${API_BASE_URL}/content/public`,
    publicById: (id) => `${API_BASE_URL}/content/public/${id}`,
  },
  // Customer Authentication
  customerAuth: {
    signup: `${API_BASE_URL}/customer-auth/signup`,
    login: `${API_BASE_URL}/customer-auth/login`,
    me: `${API_BASE_URL}/customer-auth/me`,
    sessionFromPersonal: `${API_BASE_URL}/customer-auth/session-from-personal`,
  },
  // Customer Profile
  customerProfile: {
    me: `${API_BASE_URL}/customer-profile/me`,
    orders: `${API_BASE_URL}/customer-profile/orders`,
    orderTimeline: (orderId) => `${API_BASE_URL}/customer-profile/orders/${orderId}/timeline`,
    conversations: `${API_BASE_URL}/customer-profile/conversations`,
    notifications: `${API_BASE_URL}/customer-profile/notifications`,
    markNotificationRead: (id) => `${API_BASE_URL}/customer-profile/notifications/${id}/read`,
    notificationsReadAll: `${API_BASE_URL}/customer-profile/notifications/read-all`,
    supportTickets: `${API_BASE_URL}/customer-profile/support-tickets`,
    supportTicket: (id) => `${API_BASE_URL}/customer-profile/support-tickets/${id}`,
    supportTicketMessages: (id) => `${API_BASE_URL}/customer-profile/support-tickets/${id}/messages`,
    supportTicketClose: (id) => `${API_BASE_URL}/customer-profile/support-tickets/${id}/close`,
    returns: `${API_BASE_URL}/customer-profile/returns`,
    returnById: (id) => `${API_BASE_URL}/customer-profile/returns/${id}`,
    activity: `${API_BASE_URL}/customer-profile/activity`,
    credits: `${API_BASE_URL}/customer-profile/credits`,
    bookings: `${API_BASE_URL}/customer-profile/bookings`,
    myEvents: `${API_BASE_URL}/customer-profile/my-events`,
    redeemCode: `${API_BASE_URL}/customer-profile/redeem-code`,
  },
  secretShop: {
    signup: `${API_BASE_URL}/secret-shop/signup`,
    loginByPhone: `${API_BASE_URL}/secret-shop/login-by-phone`,
    myAccess: `${API_BASE_URL}/secret-shop/my-access`,
    redeem: `${API_BASE_URL}/secret-shop/redeem`,
    orders: `${API_BASE_URL}/secret-shop/secret-orders`,
    myOrders: `${API_BASE_URL}/secret-shop/my-secret-orders`,
    orderMessages: (id) => `${API_BASE_URL}/secret-shop/my-secret-orders/${id}/messages`,
    myVideos: `${API_BASE_URL}/secret-shop/my-videos`,
    myPhone: `${API_BASE_URL}/secret-shop/my-phone`,
    myPassword: `${API_BASE_URL}/secret-shop/my-password`,
    myInstagram: `${API_BASE_URL}/secret-shop/my-instagram`,
    arrived: `${API_BASE_URL}/secret-shop/my-secret-orders/arrived`,
  },
  orderFeedback: `${API_BASE_URL}/order-feedback`,
  chatbot: {
    chat: `${API_BASE_URL}/chatbot/chat`,
  },
  // Services (consultation booking)
  services: {
    public: (params) => `${API_BASE_URL}/services/public?${new URLSearchParams(params).toString()}`,
    byId: (id) => `${API_BASE_URL}/services/${id}`,
  },
  // Bookings (consultation booking)
  bookings: {
    availableSlots: (params) => `${API_BASE_URL}/bookings/available-slots?${new URLSearchParams(params).toString()}`,
    availableSlotsForMe: (params) =>
      `${API_BASE_URL}/bookings/available-slots-for-me?${new URLSearchParams(params).toString()}`,
    calendarPreview: (params) =>
      `${API_BASE_URL}/bookings/calendar-preview?${new URLSearchParams(params).toString()}`,
    public: `${API_BASE_URL}/bookings/public`,
  },
  // Guidance conversation (pay-first, charge-on-selection booking)
  guidanceBookings: {
    createPaymentIntent: `${API_BASE_URL}/guidance-bookings/create-payment-intent`,
    authorize: `${API_BASE_URL}/guidance-bookings/authorize`,
    confirmSelection: (bookingId) => `${API_BASE_URL}/guidance-bookings/${bookingId}/confirm-selection`,
    requestTime: `${API_BASE_URL}/guidance-bookings/request-time`,
  },
  // Customer push notifications (separate from staff dashboard push)
  push: {
    vapidPublicKey: `${API_BASE_URL}/push/vapid-public-key`,
  },
  customerPush: {
    subscribe: `${API_BASE_URL}/customer-push/subscribe`,
    unsubscribe: `${API_BASE_URL}/customer-push/unsubscribe`,
  },
  // Connections (friends/family - Mango Personal)
  connections: `${API_BASE_URL}/connections`,
  // Social feed (YouTube/Instagram, pulled from Platform Integrations)
  socialFeed: {
    youtube: `${API_BASE_URL}/social/youtube/feed`,
    instagram: `${API_BASE_URL}/social/instagram/feed`,
  },
};

// Mango Personal app URL - for SSO (Sign in with Mango across business frontends)
export const MANGO_SSO_URL = process.env.REACT_APP_MANGO_SSO_URL || 'https://personal.mango-business.solutions';

// Helper function to get base URL (without /api/v1) for services that need it
export const getBaseUrl = () => {
  return API_BASE_URL.replace('/api/v1', '');
};

// Helper function to build query strings
export const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      query.append(key, params[key]);
    }
  });
  return query.toString();
};

if (process.env.NODE_ENV === 'development') {
  console.log('🔧 [CONFIG] API Configuration loaded:', {
    API_BASE_URL,
    BUSINESS_ID,
    environment: process.env.NODE_ENV,
  });

  if (!BUSINESS_ID) {
    console.error(
      '❌ [CONFIG] Missing REACT_APP_BUSINESS_ID. Customer signup/login will be blocked until set.'
    );
  }
}

